import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap';
import { RecordLink } from 'components/record-chip/RecordLink';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Checkbox } from 'components/ui/checkbox';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useMemo } from 'react';
import { Control, Controller, useFieldArray, UseFormSetValue } from 'react-hook-form';
import { ExistingPrepaymentForm, ExistingPrepaymentListForm } from 'types/invoices';
import { indexedFieldName } from 'utils/formPath';
import { t } from 'utils/getTranslations';

interface Props {
    paymentCurrency: string;
    isInViewMode: boolean;
    control: Control<ExistingPrepaymentListForm>;
    setValue: UseFormSetValue<ExistingPrepaymentListForm>;
    onSubmit: () => void;
}

const ExistingPaymentsList = ({ paymentCurrency, isInViewMode, control, setValue, onSubmit }: Props) => {
    // The new design names the currency once, in the header, and writes the amounts bare.
    const isNewDesign = useIsNewDesign();
    const { fields } = useFieldArray({
        control,
        name: 'existingPrepayments',
        keyName: 'formKey',
    });

    const columns = useMemo<DataTableColumnDef<ExistingPrepaymentForm>[]>(
        () => [
            {
                header: '',
                id: 'selection',
                enableSorting: false,
                meta: {
                    fixedPx: 48,
                },
                cell: ({ row }) => (
                    <Controller
                        name={indexedFieldName('existingPrepayments', row.index, 'isConnected')}
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id={`existingPayments.${row.index}.isConnected`}
                                variant="secondary"
                                ref={field.ref}
                                name={field.name}
                                onBlur={field.onBlur}
                                onCheckedChange={() => {
                                    setValue(
                                        indexedFieldName('existingPrepayments', row.index, 'isConnected'),
                                        !field.value
                                    );
                                    onSubmit();
                                }}
                                checked={field.value}
                                disabled={isInViewMode}
                            />
                        )}
                    />
                ),
            },
            {
                header: t('invoices.general-tab.existing-payments.columns.invoice-date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => <DateDefaultNowrap dateValue={getValue()} />,
            },
            {
                header: t('invoices.general-tab.existing-payments.columns.description'),
                id: 'invoiceDescription',
                accessorKey: 'invoiceDescription',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => <p className="text-body1 text-fg-secondary">{getValue() ?? ''}</p>,
            },
            {
                header: t('invoices.general-tab.existing-payments.columns.issued-for-case'),
                id: 'mainMatter',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ row }) => {
                    const matter = row.original.mainMatter;
                    if (!matter) {
                        return <p className="text-body1 text-fg-secondary">-</p>;
                    }
                    return (
                        <RecordLink
                            to={`/matter/${matter.id}`}
                            // `!`: the table's own cell-scoped `.entity-link` rule draws links as cell
                            // text, and outranks a plain utility.
                            className="entity-link text-link! w-fit no-underline hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            label={`${matter.caseNumber} ${matter.name}`}
                        />
                    );
                },
            },
            {
                header: isNewDesign
                    ? t('invoices.general-tab.existing-payments.columns.remaining-amount-currency', {
                          currency: paymentCurrency,
                      })
                    : t('invoices.general-tab.existing-payments.columns.remaining-amount'),
                id: 'remainingAmount',
                accessorKey: 'remainingAmount',
                enableSorting: false,
                meta: {
                    textAlign: 'right',
                    widthWeight: 1,
                    // The amount is nowrap and carries a sign + currency code; a managed cell clips
                    // instead of wrapping, so it needs a real floor.
                    minPx: 140,
                },
                cell: ({ getValue }) => (
                    <p className="text-body1 text-fg whitespace-nowrap">
                        {isNewDesign ? (
                            <MoneyDisplay amount={getValue() ?? 0} />
                        ) : (
                            <MoneyDisplay amount={getValue() ?? 0} currency={paymentCurrency} displaySign />
                        )}
                    </p>
                ),
            },
        ],
        [control, setValue, onSubmit, isInViewMode, paymentCurrency, isNewDesign]
    );

    if (isNewDesign && fields.length === 0) {
        return <p className="text-meta text-muted-foreground m-0">{t('empty-state.generic.title')}</p>;
    }

    return (
        <DataTable<ExistingPrepaymentForm> columns={columns} data={fields} columnConfigId="invoice-existing-payments" />
    );
};

export default ExistingPaymentsList;
